import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useThemeStore } from "../../../store/themeStore";
import { isExtraDark } from "../../../config/themes";
import { CHART_OVERLAY_BADGE_STYLE } from "./badgeStyles";

// Chrome compartilhado dos config-pills de bottom-overlay (Renko / Ponto &
// Figura): trigger clicável + open/close + click-outside + scaffold do
// flyout. Extraído das 2 cópias-espelho (dedup auditoria 2026-07-17, item
// 5a — o docstring do P&F literalmente dizia "Mirrors RenkoBrickSizeIndicator").
// O CORPO do flyout (auto/fixed/ATR/engine vs auto/fixed/reversal) é variação
// legítima e fica nos filhos (render-prop com {close}).

// Estilos de corpo compartilhados pelos dois flyouts
export const PILL_HINT_STYLE = { color: "#9ca3af", fontSize: "11px" };
export const FLYOUT_SECTION_STYLE = { marginBottom: "1rem" };
export const FLYOUT_LABEL_STYLE = {
  display: "block",
  marginBottom: "0.25rem",
  fontSize: "12px",
  color: "#9ca3af",
};
export const RADIO_ROW_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  marginBottom: "0.5rem",
};
export const RADIO_ROW_LAST_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
};
export const RADIO_LABEL_STYLE = {
  cursor: "pointer",
  fontSize: "13px",
  color: "var(--text-color, #fff)",
};
export const FLYOUT_INPUT_STYLE = { width: "100%" };

const ConfigPill = ({
  inline = false,
  title,
  triggerEvoId,
  flyoutTitle,
  trigger,
  onOpen,
  children,
}) => {
  const isExtraDarkTheme = useThemeStore((s) => isExtraDark(s.theme));
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        panelRef.current &&
        !panelRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Reseed do estado local do corpo acontece no onOpen do dono
  const handleToggle = () => {
    if (!isOpen && onOpen) onOpen();
    setIsOpen(!isOpen);
  };

  const close = () => setIsOpen(false);

  return (
    <div style={{ position: "relative" }}>
      <div
        ref={triggerRef}
        onClick={handleToggle}
        style={{
          ...(inline
            ? {}
            : {
                position: "absolute",
                bottom: "35px",
                left: "8px",
                zIndex: 100,
              }),
          backgroundColor: isExtraDarkTheme
            ? "rgba(9, 14, 25, 0.95)" // Extra Dark navy
            : "rgba(31, 41, 55, 0.95)",
          color: "#60a5fa",
          ...CHART_OVERLAY_BADGE_STYLE,
          cursor: "pointer",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
          border: "1px solid rgba(96, 165, 250, 0.3)",
          display: "flex",
          alignItems: "center",
          gap: "4px",
        }}
        title={title}
        data-evo-id={triggerEvoId}
      >
        {trigger}
      </div>

      {/* Flyout acima do trigger */}
      {isOpen && (
        <div
          ref={panelRef}
          style={{
            position: "absolute",
            bottom: inline ? "40px" : "75px",
            left: "0",
            width: "260px",
            backgroundColor: "var(--surface-overlay, #1f2937)",
            borderRadius: "8px",
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.5)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            zIndex: 1000,
            padding: "1rem",
          }}
        >
          <h4
            style={{
              margin: "0 0 1rem 0",
              fontSize: "14px",
              fontWeight: 600,
              color: "var(--text-color, #fff)",
            }}
          >
            {flyoutTitle}
          </h4>
          {children({ close })}
        </div>
      )}
    </div>
  );
};

ConfigPill.propTypes = {
  inline: PropTypes.bool,
  title: PropTypes.string.isRequired,
  triggerEvoId: PropTypes.string.isRequired,
  flyoutTitle: PropTypes.node.isRequired,
  trigger: PropTypes.node.isRequired,
  onOpen: PropTypes.func,
  children: PropTypes.func.isRequired,
};

export default ConfigPill;
